// Stocky の公式export・保存済みCSV・画面貼り付けから、取得できた範囲を保存する。
// ファイルや画面にない項目は補完しない。
import {
  Banner,
  BlockStack,
  Box,
  Layout,
  Link,
  Page,
  Tabs,
  Text,
} from "@shopify/polaris";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { CsvImportPanel } from "../../components/CsvImportPanel";
import {
  type AuthoritativeShopState,
  useAuthoritativeShop,
} from "../../hooks/useAuthoritativeShop";
import { t } from "../../lib/i18n";
import { ManualSupplierFallback } from "./ManualSupplierFallback";
import { ManualTaxTypeImport } from "./ManualTaxTypeImport";
import { MoqPasteImport } from "./MoqPasteImport";
import { OfficialExportImport } from "./OfficialExportImport";
import { StockyHistoryImport } from "./StockyHistoryImport";
import { StockyMigrationRouteSummary } from "./StockyMigrationRoutes";
import { StockyPortability } from "./StockyPortability";
import { StocktakeHistoryImport } from "./StocktakeHistoryImport";
import { REORDER_POLICY_SPEC } from "./specs";

/**
 * 取り込みで中身が変わり得る一覧のクエリキー。系統ごとに厳密に分けず、どれかを
 * 取り込んだら全部を無効化する（再取得は安いが、古い一覧を見せる方は高い）。
 */
const AFFECTED_QUERY_KEYS = [
  ["po-suppliers"],
  ["po-unassigned"],
  ["purchase-order-drafts"],
  ["variant-suppliers"],
  ["variant-policies"],
  ["reorder-policies"],
  ["variant-policies-moq"],
  ["tax-rates"],
  // 棚卸しの履歴を取り込むと一覧に1件増える（Stocktakes 画面と同じキー）。
  ["stocktakes"],
] as const;

export function StockyCsvImport() {
  return <StockyCsvImportBoundary />;
}

export function StockyCsvImportCopied() {
  return <StockyCsvImportBoundary />;
}

export function StockyCsvImportWithoutCopy() {
  return <StockyCsvImportBoundary />;
}

function StockyCsvImportBoundary() {
  const shop = useAuthoritativeShop();
  if (shop.shopGeneration === null) {
    return (
      <ManualStockyCopyPage>
        <StockyCsvImportShopStatusBanner shop={shop} />
      </ManualStockyCopyPage>
    );
  }
  // Every dry-run token, result and source occurrence UUID below belongs to
  // exactly one authenticated shop generation.  Remount all child workflows
  // at the boundary so an old slow response cannot populate the next shop.
  return <StockyCsvImportForShop key={shop.shopGeneration} />;
}

function ManualStockyCopyPage({ children }: { children: ReactNode }) {
  return (
    <Page
      title={t("CSV取り込み")}
      subtitle={t(
        "Stockyの公式エクスポート、保存済みCSV、画面表から取得できた範囲を保存します。",
      )}
    >
      {children}
    </Page>
  );
}

function StockyCsvImportForShop() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const selected = new URLSearchParams(location.search).get("era") === "legacy"
    ? 0
    : 1;
  const tabs = [
    {
      id: "stocky-csv-legacy",
      content: t("〜8/31"),
      panelID: "stocky-csv-panel-legacy",
    },
    {
      id: "stocky-csv-official-export",
      content: t("9/1〜"),
      panelID: "stocky-csv-panel-official-export",
    },
  ];
  const handleOfficialImported = useCallback(() => {
    for (const queryKey of AFFECTED_QUERY_KEYS) {
      void queryClient.invalidateQueries({ queryKey });
    }
    void queryClient.invalidateQueries({
      queryKey: ["stocky-migration", "source-archives"],
    });
    void queryClient.invalidateQueries({ queryKey: ["stocky-data"] });
  }, [queryClient]);

  const selectTab = useCallback(
    (index: number) => {
      if (index !== 0 && index !== 1) return;
      const params = new URLSearchParams(location.search);
      if (index === 0) params.set("era", "legacy");
      else params.delete("era");
      navigate(
        {
          pathname: location.pathname,
          search: params.toString(),
          hash: location.hash,
        },
        { replace: true },
      );
    },
    [location.hash, location.pathname, location.search, navigate],
  );

  return (
    <ManualStockyCopyPage>
      <Tabs tabs={tabs} selected={selected} onSelect={selectTab}>
        <Box paddingBlockStart="400">
          {selected === 0 ? (
            <StockyCsvImportWorkflow />
          ) : (
            <OfficialExportImport onImported={handleOfficialImported} />
          )}
        </Box>
      </Tabs>
    </ManualStockyCopyPage>
  );
}

export function StockyCsvImportShopStatusBanner({
  shop,
}: {
  shop: AuthoritativeShopState;
}) {
  return (
    <Banner
      tone={shop.isError ? "critical" : "info"}
      title={
        shop.isError
          ? t("現在のショップを確認できません")
          : t("現在のショップを確認しています")
      }
    >
      <Text as="p">
        {shop.isError
          ? t("再読み込みするか、ショップを開き直してから再試行してください。")
          : t("ショップを確認できるまで移行入力は開始しません。")}
      </Text>
      {shop.isError && (
        <Link onClick={shop.retry}>{t("もう一度確認する")}</Link>
      )}
    </Banner>
  );
}

export function StockyCsvImportWorkflow() {
  const queryClient = useQueryClient();

  const handleImported = useCallback(() => {
    for (const queryKey of AFFECTED_QUERY_KEYS) {
      void queryClient.invalidateQueries({ queryKey });
    }
  }, [queryClient]);

  return (
    <Layout>
      <Layout.Section>
        {/* Stocky migration と portability は Free。課金状態の取得失敗でも
            マーチャント自身の原本・移行データを塞がないため PlanGate を置かない。 */}
        <BlockStack gap="400">
          <Banner tone="warning" title={t("Stockyデータ取込について")}>
            <Text as="p">
              {t(
                "Stocky公式エクスポートや終了前に保存したCSV・画面表のうち、下に表示する対応形式だけを取り込めます。ファイルにない項目は推測して補完しません。",
              )}
            </Text>
          </Banner>
          <Banner
            tone="info"
            title={t("Stocky公式エクスポートを使う場合")}
          >
            <BlockStack gap="200">
              <Text as="p">
                {t(
                  "Stockyから届いたファイルを確認し、下に示すファイル名・列と一致するものだけを選んでください。エクスポート全体のZIPをそのまま取り込む機能ではありません。",
                )}
              </Text>
            </BlockStack>
          </Banner>

          <StockyMigrationRouteSummary />

          <section
            id="stocky-required-imports"
            aria-labelledby="stocky-required-imports-title"
          >
            <BlockStack gap="400">
              <BlockStack gap="100">
                <Text
                  as="h2"
                  id="stocky-required-imports-title"
                  variant="headingLg"
                >
                  {t("Low Stock・発注点／リードタイム・棚卸し履歴・明細・最低発注数量（MOQ）")}
                </Text>
                <Text as="p" tone="subdued">
                  {t(
                    "次のCSVまたは保存済み画面表を項目ごとに取り込みます。",
                  )}
                </Text>
              </BlockStack>

              <CsvImportPanel
                spec={REORDER_POLICY_SPEC}
                onImported={handleImported}
              />

              {/* 棚卸しの履歴だけ別コンポーネント＝ファイルごとに実施日が要り、
                    複数の棚卸しをまとめて取り込む（Stocky は棚卸し1回につき1つの
                    CSV しか書き出さないので、履歴のある店ほどファイルが増える）。 */}
              <StocktakeHistoryImport onImported={handleImported} />

              {/* 🔴 MOQ だけ CSV ではなく貼り付け＝Stocky に書き出しが無く、画面を
                    コピーすると数量が別行に落ちるため（2026-08-01 実測）。整形を
                    マーチャントにやらせない。 */}
              <MoqPasteImport onImported={handleImported} />
            </BlockStack>
          </section>

          <section
            id="stocky-manual-history-imports"
            aria-labelledby="stocky-manual-history-imports-title"
          >
            <BlockStack gap="400">
              <BlockStack gap="100">
                <Text
                  as="h2"
                  id="stocky-manual-history-imports-title"
                  variant="headingLg"
                >
                  {t("仕入先・仕入用税区分・商品と仕入先の紐付け・発注書・明細・在庫調整・明細")}
                </Text>
                <Text as="p" tone="subdued">
                  {t(
                    "すでに取り込み済みの場合、この入力は不要です。追加するCSVがある場合だけ、下の各欄で選んでください。",
                  )}
                </Text>
              </BlockStack>
              <ManualSupplierFallback onImported={handleImported} />
              <ManualTaxTypeImport onImported={handleImported} />
              <StockyHistoryImport onImported={handleImported} />
            </BlockStack>
          </section>

          <section
            id="stocky-saved-sources"
            aria-label={t("Stock on hand・CSVで書き出せるレポート原本")}
          >
            <BlockStack gap="400">
              <Banner tone="info" title={t("Transfersは保存作業不要です")}>
                <Text as="p">
                  {t(
                    "Stockyの「Inventory → Transfers」は外部リンクでShopify管理画面の「在庫移管」を開きます。StockyのレポートやCSVは表示されないため、この移行画面でTransfersを探したりアップロードしたりする必要はありません。",
                  )}
                </Text>
              </Banner>
              <StockyPortability />
            </BlockStack>
          </section>
        </BlockStack>
      </Layout.Section>
    </Layout>
  );
}
